import type { Camera } from '@strava/mre';

import type { LatLng, ScreenPosition } from '../types';
import { getPixelOffsetFromCoordinates } from './getPixelOffsetFromCoordinates';

/*
 * Helper to get the screen position (in pixels) of a latLng relative to a container.
 *
 * If the point is on-screen (within the bounds of the container), returns the pixel position within the container.
 *
 * If the point is off-screen, position is calculated relative to current camera center.
 * This is required when positioning HTML elements that may be (partially)
 * off-screen, e.g. Privacy Zones
 *
 * NOTE: We could ALWAYS use the relative calculation (including for on-screen points),
 * but the camera's built-in `getScreenPosition` should be more performant and consistent.
 */

interface GetScreenPositionOfLatLng {
  container: HTMLElement; // The container element to calculate positions relative to
  latLng: LatLng; // The target LatLng
  camera: Camera; // The MRE camera instance
}

export const getScreenPositionOfLatLng = ({
  container,
  latLng,
  camera
}: GetScreenPositionOfLatLng): ScreenPosition | null => {
  // Get container dimensions
  const { clientWidth: containerWidth, clientHeight: containerHeight } =
    container;

  // Get lat + lng of the target point
  const [targetLat, targetLng] = latLng;

  // Get the screen position, i.e. the point within the screen bounds of the target
  const screenPosition = camera.getScreenPosition({
    latitude: targetLat,
    longitude: targetLng
  });

  // if `screenPosition` is defined, return the pixel position within the container
  if (screenPosition) {
    return {
      x: screenPosition.x * containerWidth, // convert from normalized to pixel coordinates
      y: screenPosition.y * containerHeight, // convert from normalized to pixel coordinates
      isOccluded: screenPosition.isOccluded // pass through occlusion status
    };
  }

  // If `screenPosition` is `undefined`, calculate off-screen position
  const { lookAtPoint } = camera.getTarget(); // get camera target lookAtPoint
  const { latitude: cameraLat, longitude: cameraLng } = lookAtPoint; // lat + lng of the camera center
  const scale = camera.getScaleMetersPerPixel(); // get current scale in meters per pixel

  // Guard against missing or invalid camera data
  if (
    typeof cameraLat !== 'number' ||
    typeof cameraLng !== 'number' ||
    !scale
  ) {
    throw new Error(
      'Unable to calculate off-screen position due to missing camera data.'
    );
  }

  // Calculate pixel offset from camera center to target latLng
  const { deltaX, deltaY } = getPixelOffsetFromCoordinates(
    cameraLat, // from camera center lat
    cameraLng, // from camera center lng
    targetLat, // to target lat
    targetLng, // to target lng
    scale // current scale in meters per pixel
  );

  // Return position relative to container center
  return {
    x: containerWidth / 2 + deltaX, // center of container + deltaX
    y: containerHeight / 2 + deltaY // center of container + deltaY
  };
};
